import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Divider,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DialogContainer from "../../../../components/dialog-container/dialog-container";
import InputNumber from "../../../../components/input-number/input-number";

interface AdjustmentData {
  id: string;
  date: string;
  type: string;
  adjustment: string;
  amount: number;
  note: string;
  category: string;
}

interface ValidationErrors {
  date?: string;
  type?: string;
  adjustment?: string;
  amount?: string;
}

interface AdjustmentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AdjustmentData) => void;
  onDelete?: (id: string) => void;
  mode: "add" | "edit";
  adjustmentType: "client" | "inspector";
  editData?: AdjustmentData;
  adjustmentOptions: any[];
}

const AdjustmentModal = ({
  open,
  onClose,
  onSave,
  onDelete,
  mode,
  adjustmentType,
  editData,
  adjustmentOptions,
}: AdjustmentModalProps) => {
  const [formData, setFormData] = useState<AdjustmentData>({
    id: "",
    date: "",
    type: "",
    adjustment: "",
    amount: 0,
    note: "",
    category: adjustmentType === "client" ? "ClientFee" : "InspectorFee",
  });
  const [useTodayDate, setUseTodayDate] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    if (open) {
      if (editData && mode === "edit") {
        setFormData(editData);
        setUseTodayDate(false);
      } else {
        setFormData({
          id: "",
          date: "",
          type: "PreBillingAdjustment",
          adjustment: "",
          amount: 0,
          note: "",
          category: adjustmentType === "client" ? "ClientFee" : "InspectorFee",
        });
        setUseTodayDate(false);
      }
      setErrors({});
    }
  }, [open, editData, mode, adjustmentType]);

  useEffect(() => {
    if (useTodayDate) {
      const today = dayjs().format("YYYY-MM-DD");
      setFormData((prev) => ({ ...prev, date: today }));
    }
  }, [useTodayDate]);

  const handleInputChange = (field: keyof AdjustmentData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof ValidationErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.date) {
      newErrors.date = "This field can't be empty";
    }

    if (!formData.type) {
      newErrors.type = "This field can't be empty";
    }

    if (formData.type === "PreBillingAdjustment" && !formData.adjustment) {
      newErrors.adjustment = "This field can't be empty";
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      const dataToSave = {
        ...formData,
        id: mode === "edit" ? formData.id : Date.now().toString(),
        adjustment:
          formData.type === "PreBillingAdjustment"
            ? formData.adjustment
            : "n/a",
      };
      onSave(dataToSave);
      handleClose();
    }
  };

  const handleDelete = () => {
    if (onDelete && formData.id) {
      onDelete(formData.id);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      id: "",
      date: "",
      type: "PreBillingAdjustment",
      adjustment: "",
      amount: 0,
      note: "",
      category: adjustmentType === "client" ? "ClientFee" : "InspectorFee",
    });
    setUseTodayDate(false);
    setErrors({});
    onClose();
  };

  const getTypeOptions = () => {
    return ["Billing", "Cost", "PreBillingAdjustment"];
  };

  return (
    <DialogContainer
      title={`${
        adjustmentType === "client" ? "Client" : "Inspector"
      } Adjustment`}
      showDialog={open}
      closeDialog={handleClose}
      onSubmit={handleSave}
      buttonText="SAVE"
      leftButtonComponent={
        mode === "edit" &&
        formData.type === "PreBillingAdjustment" &&
        onDelete && (
          <Button
            sx={{ mr: 2 }}
            startIcon={<DeleteOutlinedIcon color="error" />}
            color="error"
            onClick={handleDelete}
          >
            DELETE
          </Button>
        )
      }
    >
      <form onSubmit={handleSave}>
        <Box className="user-name-layout">
          <Typography className="label-txt">Date</Typography>
          <Box sx={{ width: "calc(100% - 192px)" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useTodayDate}
                  onChange={(e) => setUseTodayDate(e.target.checked)}
                  sx={{
                    color: "primary.main",
                    "&.Mui-checked": {
                      color: "primary.main",
                    },
                  }}
                />
              }
              label="Today's Date"
            />
            <Box sx={{ mt: 2, width: "320px" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date"
                  sx={{
                    width: "100% !important",
                    height: 56,
                    "& .MuiInputBase-root": {
                      "& .MuiInputBase-input": {
                        padding: 2,
                      },
                    },
                    pb: 1,
                  }}
                  value={formData.date ? dayjs(formData.date) : null}
                  onChange={(date) => {
                    if (date) {
                      handleInputChange("date", date.format("YYYY-MM-DD"));
                      setUseTodayDate(false);
                    }
                  }}
                  format="MMMM D, YYYY"
                  slotProps={{
                    inputAdornment: {
                      position: "start",
                    },
                    textField: {
                      variant: "outlined",
                      placeholder: "Select Date",
                      inputProps: {
                        readOnly: true,
                        style: { cursor: "pointer" },
                      },
                      error: !!errors.date,
                      helperText: errors.date,
                    },
                  }}
                  disabled={useTodayDate}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Type</Typography>
          <Box sx={{ width: "calc(100% - 192px)" }}>
            <Box>
              <FormControl fullWidth>
                <Select
                  value={formData.type}
                  onChange={(e) => handleInputChange("type", e.target.value)}
                  label=""
                  sx={{ height: 56 }}
                  disabled={true}
                  error={!!errors.type}
                >
                  {getTypeOptions().map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.type && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, display: "block" }}
                >
                  {errors.type}
                </Typography>
              )}
            </Box>
            {formData.type === "PreBillingAdjustment" && (
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Adjustment</InputLabel>
                  <Select
                    value={formData.adjustment}
                    onChange={(e) =>
                      handleInputChange("adjustment", e.target.value)
                    }
                    label="Adjustment"
                    sx={{ height: 56 }}
                    error={!!errors.adjustment}
                  >
                    {adjustmentOptions?.map((option) => (
                      <MenuItem
                        key={option.adjustmentType}
                        value={option.adjustmentType}
                      >
                        {option.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors.adjustment && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.adjustment}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Divider />

        <Box className="user-name-layout">
          <Typography className="label-txt">Amount</Typography>
          <Box sx={{ width: "calc(100% - 192px)", mb: 1 }}>
            <InputNumber
              fullWidth
              size="small"
              label="Amount"
              value={formData.amount}
              onChange={(e) => handleInputChange("amount", e.target.value)}
              placeholder="0.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              error={!!errors.amount}
              helperText={errors.amount}
            />
          </Box>
        </Box>
        <Divider />

        <Box className="user-name-layout">
          <Typography className="label-txt">Note</Typography>
          <Box sx={{ width: "calc(100% - 192px)" }}>
            <TextField
              value={formData.note}
              onChange={(e) => handleInputChange("note", e.target.value)}
              variant="outlined"
              fullWidth
              placeholder="Add Note"
            />
          </Box>
        </Box>
      </form>
    </DialogContainer>
  );
};

export default AdjustmentModal;
